/**
Read MIDI data from an array buffer
*/

type State = {
  data: Uint8Array
  position: number
}

// was read in stream.js
readAscii := (state: State, length: number) ->
  {data, position} := state
  state.position = position + length

  return String.fromCharCode.apply(String, data.slice(position, position + length) as! number[])

subarray := (state: State, length: number) ->
  {data, position} := state
  state.position = position + length

  return data.subarray(position, position + length)

// read a big-endian 32-bit integer
readInt32 := (state: State) ->
  {data, position} := state
  state.position = position + 4

  return (
    (data[position] << 24) +
    (data[position + 1] << 16) +
    (data[position + 2] << 8) +
     data[position + 3])

readInt24 := (state: State) ->
  {data, position} := state
  state.position = position + 3

  return (
    (data[position] << 16) +
    (data[position + 1]<< 8) +
     data[position + 2])

// read a big-endian 16-bit integer
readInt16 := (state: State) ->
  {data, position} := state
  state.position = position + 2

  return (
    (data[position] << 8) +
     data[position + 1])

// read an 8-bit integer
readInt8 := (state: State, signed?: boolean) ->
  {data, position} := state
  state.position = position + 1

  result .= data[position]
  if signed and result > 127
    result -= 256

  return result

/**
read a MIDI-style variable-length integer
(big-endian value in groups of 7 bits,
with top bit set to signify that another byte follows)

The spec caps a variable-length quantity at FOUR bytes (28 bits). That cap is
load-bearing, not pedantry: this used to `loop` unbounded over `result <<= 7`,
and `<<` in JavaScript is 32-bit SIGNED, so five or more bytes with the high bit
set overflow into the sign bit and the function returns a NEGATIVE length. A
caller that does `position += length` then walks BACKWARDS, and any enclosing
"while position < end" spins forever without advancing.

That is not hypothetical — the same expression, copied into the social scripts'
own reader, span a core for 51 minutes on a real archive file
(lmd_full/b/bdf8f5834f7b685a9ac7360e00835223.mid) whose 19 MTrk chunks are
otherwise perfectly well formed.

So: multiply instead of shift, so a legal 28-bit value can never go negative;
stop after four bytes; and stop at the end of the data rather than reading
`undefined` off the tail and returning NaN.
*/
readVarInt := (state: State) ->
  result .= 0
  for i of [0...4]
    if state.position >= state.data.length
      break
    b := readInt8(state)
    result = result * 128 + (b & 0x7f)
    unless b & 0x80
      break

  return result

readChunk := (state: State) ->
  id := readAscii(state, 4)
  length := readInt32(state)
  sub := subarray(state, length)

  return {
    id: id
    length: length
    data: sub
    position: 0
  }

// Read an event returning the deltaTime, the event type, and the raw event data
// advancing the state position to the next event. The event type may depend on
// the previous state for the case of running status messages.
readEvent := (state: Track) ->
  // Consume the deltaTime
  deltaTime := readVarInt(state)

  // Find the start position of the data
  start := state.position
  type .= readInt8(state)

  let raw
  switch type
    when 0xff // meta
      metaType := readInt8(state)
      length := readVarInt(state)

      offset := state.position - start
      state.position = start
      raw = subarray(state, length + offset)

    when 0xf0, 0xf7 //
      length := readVarInt(state)

      offset := state.position - start
      state.position = start
      raw = subarray(state, length + offset)

    else // channel event
      if (type & 0x80) is 0
        // running status - reuse lastEventType as the event type.
        // eventTypeByte is actually the first parameter
        // param1 = type
        type = state.lastEventType
      else
        readInt8(state) // param1
        // State is updated with last event type
        state.lastEventType = type

      offset := state.position - start
      state.position = start

      switch (type >> 4)
        // Program Change and Channel Pressure only have one data byte
        when 0xc, 0xd
          raw = subarray(state, offset)
        else // The rest have two
          raw = subarray(state, offset + 1)

  // type may be the running status from state in which case it won't match the
  // first data byte
  return {
    deltaTime: deltaTime
    type: type
    data: raw
  }

// Move the next event into the current property of state, advancing track state
// and resetting ticks
advanceTrack := (state: Track): void ->
  // A track past the end of its data has no next event. This is checked HERE, explicitly,
  // because it used to be checked by accident: readVarInt read `undefined` off the tail and
  // returned NaN, the NaN flowed into `current.deltaTime`, and `at < minTicks` below is
  // false for NaN — so an exhausted track simply never won the min-delta race. Capping
  // readVarInt (c3d45e0b) made it return 0 at the tail instead, which is the RIGHT value for
  // a varint and the worst possible one for a sentinel: a zero delta always wins that race,
  // so every MIDI file ran away emitting infinite zero-tick events until the heap died.
  state.current = if state.position >= state.data.length
    null as! TrackEvent
  else
    readEvent(state)
  state.ticks = 0

nextTrackEvent := (state: StateSnapshot) ->
  {formatType, tracks} := state

  if formatType is 0 or formatType is 1
    minTicks .= Infinity
    let nextTrack: Track?
    trackIndex .= 0

    // Find track with minimum delta time
    tracks.forEach (track, i) ->
      // Exhausted tracks (current: null) drop out of the race; when every track is
      // exhausted nextTrack stays undefined and the caller's loop ends.
      return unless track.current
      at := track.current.deltaTime - track.ticks

      if at < minTicks
        trackIndex = i
        minTicks = at
        nextTrack = track

    // Read the event
    if nextTrack
      // Update ticks
      tracks.forEach (track) ->
        track.ticks += minTicks

      // Advance the track
      event := nextTrack.current
      advanceTrack nextTrack

      // adjust delta time on event
      event.deltaTime = minTicks
      event.track = trackIndex

      return event
  else
    // Independent tracks
    throw "MIDI format type #{formatType} not supported yet"

export class MidiFile
  lastEventType = 0

  formatType = -1
  ticksPerBeat = -1
  tracks
  state

  @(data: Uint8Array)
    @state = {
      data,
      position: 0
    }

    header := readChunk @state

    if header.id != 'MThd' or header.length != 6
      /* c8 ignore next */
      throw 'MIDI header not found'

    @formatType = readInt16 header
    trackCount := readInt16 header
    timeDivision := readInt16 header

    if timeDivision & 0x8000
      /* c8 ignore next */
      throw 'Expressing time division in SMTPE frames is not supported yet'
    else
      @ticksPerBeat = timeDivision

    // Need track data so we can process track events
    @tracks = [0...trackCount].map =>
      trackChunk := readChunk(@state)
      if trackChunk.id != 'MTrk'
        /* c8 ignore next */
        throw 'Unexpected chunk - expected MTrk, got ' + trackChunk.id

      // Each track has its own data and position
      track :=
        current: null as! TrackEvent
        data: trackChunk.data
        position: 0
        ticks: 0
        lastEventType: 0

      // Initialize the track state with the first event
      advanceTrack track

      return track

type StateSnapshot =
  formatType: number
  ticksPerBeat: number
  tracks: Track[]

/**
Create a snapshot of the midiFile state that can be saved to restart the event
stream from this location
 */
stateSnapshot := (file: StateSnapshot) ->
  {tracks, formatType, ticksPerBeat} := file

  formatType: formatType
  ticksPerBeat: ticksPerBeat
  tracks: tracks.map ({current, data, lastEventType, position, ticks}) ->
    {
      // Object.assign({}, null) is {}, which is TRUTHY — copying an exhausted track that
      // way would resurrect it as a live track with an undefined deltaTime.
      current: current and Object.assign({}, current)
      data
      position
      ticks
      lastEventType
    }

export {
  nextTrackEvent
  readEvent
  readInt8
  readInt16
  readInt24
  readInt32
  readVarInt
  readAscii
  stateSnapshot
  subarray
}

type Track = {
  current: TrackEvent
  data: Uint8Array
  position: number
  ticks: number
  lastEventType: number
}

type TrackEvent = {
  deltaTime: number
  type: number
  data: Uint8Array
  track?: number
}
